import { OrbindApiError } from '@orbind/api-client'
import { Alert, Button, Input, Modal } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../lib/api.ts'
import { tirarItemDosQuadros } from './api.ts'

/**
 * EXCLUSÃO DEFINITIVA de um Item de Trabalho
 * (SPEC-20261008-1649-excluir-e-arquivar-item, pedido do Cristian: "excluir
 * exclui definitivamente, deve até ter o esquema de colocar a confirmação
 * digitando o código do item").
 *
 * Diferente do ARQUIVAR (que só marca e tem volta): aqui o item some do
 * banco, com comentários, histórico e anexos. Por isso a janela:
 * - mostra a árvore que está embaixo ANTES de a pessoa escolher;
 * - pergunta se os filhos só são DESVINCULADOS (sobem para a raiz) ou se vão
 *   junto — e lista o que será apagado em cada caso;
 * - só libera o botão com o código do item digitado (a API confere de novo).
 */
export function ExcluirDefinitivoModal({
  itemId,
  onFechar,
  onExcluido,
}: {
  itemId: string
  onFechar: () => void
  onExcluido: () => void
}) {
  const queryClient = useQueryClient()
  const previa = useQuery({
    queryKey: ['work-item', itemId, 'exclusao'],
    queryFn: async () => (await api['work-items'][':id'].exclusao.$get({ param: { id: itemId } })).json(),
  })
  const [filhos, setFilhos] = useState<'desvincular' | 'excluir'>('desvincular')
  const [confirmacao, setConfirmacao] = useState('')

  const excluir = useMutation({
    mutationFn: async () =>
      (await api['work-items'][':id'].$delete({ param: { id: itemId }, json: { confirmacao, filhos } })).json(),
    onSuccess: () => {
      // excluído: sai dos quadros na hora (SPEC-20261027-1941-quadro-mais-rapido)
      tirarItemDosQuadros(queryClient, itemId)
      void queryClient.invalidateQueries({ queryKey: ['work-items'] })
      void queryClient.invalidateQueries({ queryKey: ['work-item'] })
      onExcluido()
    },
  })

  const dados = previa.data
  const temFilhos = (dados?.descendentes.length ?? 0) > 0
  const apagados = dados ? [{ ...dados.item, nivel: 0, arquivado: false }, ...(filhos === 'excluir' ? dados.descendentes : [])] : []
  const anexos = dados ? (filhos === 'excluir' ? dados.arquivos.daArvore : dados.arquivos.doItem) : 0
  const codigoConfere = dados !== undefined && confirmacao.trim().toUpperCase() === dados.item.code.toUpperCase()
  const erro =
    excluir.error instanceof OrbindApiError || excluir.error instanceof Error ? excluir.error.message : null

  return (
    <Modal
      open
      onClose={excluir.isPending ? () => {} : onFechar}
      title="Excluir definitivamente"
      width={540}
      footer={
        <>
          <Button variant="ghost" onClick={onFechar} disabled={excluir.isPending}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            icon="fas fa-trash-alt"
            disabled={!codigoConfere || excluir.isPending}
            loading={excluir.isPending}
            onClick={() => excluir.mutate()}
          >
            {apagados.length > 1 ? `Excluir ${apagados.length} itens` : 'Excluir definitivamente'}
          </Button>
        </>
      }
    >
      <div className="wi-excluir">
        {previa.isPending ? <p className="wi-excluir__texto">Conferindo o que está embaixo deste item…</p> : null}
        {previa.isError ? (
          <Alert tone="danger" title="Não foi possível conferir o item">
            Feche e tente de novo. Sem saber o que está embaixo, a exclusão não segue.
          </Alert>
        ) : null}
        {dados ? (
          <>
            <Alert tone="danger" title="Esta ação não tem volta">
              O item some de vez, com comentários, histórico, checklists e anexos. Para só tirar das listas e poder
              recuperar depois, use <strong>Arquivar</strong>.
            </Alert>

            {temFilhos ? (
              <div className="wi-excluir__escolha" role="radiogroup" aria-label="O que fazer com os itens filhos">
                <button
                  type="button"
                  role="radio"
                  aria-checked={filhos === 'desvincular'}
                  className={`wi-excluir__opcao${filhos === 'desvincular' ? ' wi-excluir__opcao--on' : ''}`}
                  onClick={() => setFilhos('desvincular')}
                >
                  <i className={filhos === 'desvincular' ? 'fas fa-dot-circle' : 'far fa-circle'} aria-hidden />
                  <span>
                    <strong>Só desvincular os filhos</strong>
                    <small>
                      {dados.filhosDiretos === 1
                        ? 'O filho direto sobe para a raiz, com tudo o que está embaixo dele.'
                        : `Os ${dados.filhosDiretos} filhos diretos sobem para a raiz, com tudo o que está embaixo deles.`}{' '}
                      Só este item é excluído.
                    </small>
                  </span>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={filhos === 'excluir'}
                  className={`wi-excluir__opcao${filhos === 'excluir' ? ' wi-excluir__opcao--on' : ''}`}
                  onClick={() => setFilhos('excluir')}
                >
                  <i className={filhos === 'excluir' ? 'fas fa-dot-circle' : 'far fa-circle'} aria-hidden />
                  <span>
                    <strong>Excluir os filhos também</strong>
                    <small>
                      A árvore inteira abaixo dele é excluída: {dados.descendentes.length}{' '}
                      {dados.descendentes.length === 1 ? 'item' : 'itens'}
                      {dados.descendentes.some((d) => d.arquivado) ? ', inclusive os arquivados' : ''}.
                    </small>
                  </span>
                </button>
              </div>
            ) : null}

            <div>
              <p className="wi-excluir__rotulo">
                Será excluído{apagados.length > 1 ? `s (${apagados.length})` : ''}:
              </p>
              <ul className="wi-excluir__lista">
                {apagados.map((a) => (
                  <li key={a.id} style={{ paddingLeft: a.nivel * 16 }}>
                    <span className="wi-cl__wicode">{a.code}</span>
                    <span className="wi-excluir__titulo">{a.title}</span>
                    {a.arquivado ? (
                      <span className="wi-arquivado-selo">
                        <i className="fas fa-archive" aria-hidden /> Arquivado
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
              {anexos > 0 ? (
                <p className="wi-excluir__texto">
                  <i className="fas fa-paperclip" aria-hidden /> {anexos} {anexos === 1 ? 'anexo é apagado' : 'anexos são apagados'}{' '}
                  junto.
                </p>
              ) : null}
            </div>

            <label className="wi-excluir__confirma">
              <span>
                Para confirmar, digite o código <strong>{dados.item.code}</strong>
              </span>
              <Input
                autoFocus
                value={confirmacao}
                placeholder={dados.item.code}
                onChange={(e) => setConfirmacao(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && codigoConfere && !excluir.isPending) excluir.mutate()
                }}
              />
            </label>

            {erro ? (
              <Alert tone="danger" title="Não foi possível excluir">
                {erro}
              </Alert>
            ) : null}
          </>
        ) : null}
      </div>
    </Modal>
  )
}
